import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { LABEL } from "./kwShared";

const SEL = "w-full rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm";
const DEPTH_LABEL = { 10: "Top 10 (page 1)", 20: "Top 20", 50: "Top 50", 100: "Top 100" };
const estimate = (kws, devices, depth) => { const pages = Math.max(1, depth / 10); return kws * Math.max(1, devices) * 30 * 0.0006 * (1 + (pages - 1) * 0.75); };

export function KeywordSettingsDialog({ wid, brand, brandName, settings, packages, depths, onClose, onSaved }) {
  const [form, setForm] = useState({ ...settings, competitors: [...(settings.competitors || []), "", "", ""].slice(0, 3) });
  const [countries, setCountries] = useState([]);
  const [account, setAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/console/keywords/locations").then(({ data }) => setCountries(data.countries || [])).catch(() => {});
    api.get("/console/keywords/account").then(({ data }) => setAccount(data)).catch((e) => setAccount({ error: formatApiError(e.response?.data?.detail) }));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDevice = (d) => set("devices", form.devices.includes(d) ? form.devices.filter((x) => x !== d) : [...form.devices, d]);
  const save = async () => {
    setSaving(true);
    const loc = countries.find((c) => c.code === Number(form.location_code));
    try {
      await api.put(`/console/keywords/${wid}/settings`, { ...form, location_code: Number(form.location_code), location_name: loc?.name || form.location_name, package: Number(form.package), depth: Number(form.depth), competitors: form.competitors.map((c) => c.trim()).filter(Boolean) }, { params: brand ? { brand } : {} });
      toast.success("Keyword settings saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const cost = estimate(Number(form.package) || 0, form.devices.length, Number(form.depth));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="kw-settings-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Keyword tracking settings</DialogTitle><DialogDescription>{brandName ? `Settings for the ${brandName} brand. ` : ""}Package, website and where rankings are checked from.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className={`${LABEL} mb-1.5 block`}>Package</label>
            <div className="flex flex-wrap gap-1.5" data-testid="kw-settings-package">
              {packages.map((p) => (
                <button key={p} data-testid={`kw-package-${p}`} onClick={() => set("package", p)}
                  className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${Number(form.package) === p ? "bg-[#06060C] text-white border-[#06060C]" : "border-[#EEEFEF] text-[#595D62] hover:bg-[#F6F6FD]"}`}>{p === 0 ? "Off" : `${p} keywords`}</button>
              ))}
            </div>
          </div>
          <div><label className={`${LABEL} mb-1 block`}>{brandName ? "Brand website" : "Client website"}</label><Input data-testid="kw-settings-domain" value={form.domain} onChange={(e) => set("domain", e.target.value)} placeholder={brandName ? "brand.com.au" : "client.com.au"} className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={`${LABEL} mb-1 block`}>Country</label>
              <select data-testid="kw-settings-country" value={form.location_code} onChange={(e) => set("location_code", e.target.value)} className={SEL}>
                {(countries.length ? countries : [{ code: form.location_code, name: form.location_name }]).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select></div>
            <div><label className={`${LABEL} mb-1 block`}>Search depth</label>
              <select data-testid="kw-settings-depth" value={form.depth} onChange={(e) => set("depth", e.target.value)} className={SEL}>
                {depths.map((d) => <option key={d} value={d}>{DEPTH_LABEL[d]}</option>)}
              </select></div>
          </div>
          <div>
            <label className={`${LABEL} mb-1.5 block`}>Devices</label>
            <div className="flex gap-4">
              {["desktop", "mobile"].map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm capitalize cursor-pointer"><input data-testid={`kw-device-${d}`} type="checkbox" checked={form.devices.includes(d)} onChange={() => toggleDevice(d)} className="accent-[#723FFF]" />{d}</label>
              ))}
            </div>
          </div>
          <div>
            <label className={`${LABEL} mb-1 block`}>Competitors (up to 3)</label>
            <div className="space-y-2">
              {form.competitors.map((c, i) => <Input key={i} data-testid={`kw-competitor-${i}`} value={c} onChange={(e) => set("competitors", form.competitors.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`competitor${i + 1}.com.au`} className="bg-[#F6F6FD] border-[#EEEFEF]" />)}
            </div>
          </div>
          <div className="rounded-xl bg-[#F8F5FF] border border-[#EDE7FF] px-4 py-3 text-xs text-[#595D62] space-y-1" data-testid="kw-settings-cost">
            <p>Estimated data cost at a full package: <span className="font-semibold text-[#06060C]">~US${cost.toFixed(2)}/month</span></p>
            <p>{account?.error ? <span className="text-rose-600">{account.error}</span> : account ? <>Provider balance: <span className="font-semibold text-[#06060C]">US${Number(account.balance || 0).toFixed(2)}</span> · Last 30 days: US${Number(account.spend_30d || 0).toFixed(2)}</> : "Checking balance…"}</p>
          </div>
        </div>
        <DialogFooter><Button data-testid="kw-settings-save" onClick={save} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
